import { Fragment, useState, type ReactNode } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useConnection, useMediaUrl } from '@/model';
import { Avatar, Banner, Button, Card, EmptyState, ErrorState, IconButton, ListSection, NavigationBar, Spinner, DisclosureGroup, formatDateTime } from '@/ui';
import { errorCopy, useRuntime, useWords } from './hooks';
import { object, text } from './domain';
/** Local forms and previews survive resync, and remount at every login boundary. */
export function AgentScope({ children }: {
    children: ReactNode;
}) { const { scope } = useRuntime(); return <Fragment key={scope.loginGeneration}>{children}</Fragment>; }
export function Page({ title, children, actions, subtitle }: {
    title: string;
    children: ReactNode;
    actions?: ReactNode;
    subtitle?: string;
}) {
    return <section className="agents-page"><NavigationBar title={title} subtitle={subtitle} trailing={actions}/><div className="agents-content">{children}</div></section>;
}
export function Group({ title, children, footer }: {
    title?: string;
    children: ReactNode;
    footer?: ReactNode;
}) { return <ListSection className="agents-group" title={title} footer={footer}>{children}</ListSection>; }
export function Resource<T>({ resource, children, empty }: {
    resource: {
        data: T | undefined;
        isPending: boolean;
        isFetching: boolean;
        error: unknown;
        refetch(): unknown;
        enabled?: boolean;
        waiting?: boolean;
    };
    children: (data: T) => ReactNode;
    empty?: string;
}) {
    const { w } = useWords();
    const connection = useConnection();
    if (resource.error && !resource.data)
        return <ErrorState title={w(errorCopy(resource.error))} action={<Button onClick={() => void resource.refetch()}>{w('Retry')}</Button>}/>;
    if (resource.data === undefined)
        return resource.enabled === false && !resource.waiting ? <EmptyState title={w('This is no longer available.')}/> : <div className="agents-loading"><Spinner label={w('Loading…')}/></div>;
    return <>{connection.phase === 'offline' && <Banner tone="warning" title={w('Offline. Showing the last available data.')}/>} {resource.isFetching && <Spinner label={w('Refreshing…')} size={16}/>} {resource.error && <Banner tone="warning" title={w(errorCopy(resource.error))} action={<Button size="small" onClick={() => void resource.refetch()}>{w('Retry')}</Button>}/>}{empty && <EmptyState title={empty}/>} {children(resource.data)}</>;
}
export function CommandError({ error }: {
    error: unknown;
}) { const { w } = useWords(); return error ? <Banner tone="error" title={w(errorCopy(error))}/> : null; }
export function AgentAvatar({ name, id, mediaId, size = 44, status = 'idle' }: {
    name: string;
    id: string;
    mediaId?: string | null;
    size?: number;
    status?: 'idle' | 'working' | 'needs-you';
}) {
    if (mediaId)
        return <PhotoAvatar name={name} id={id} mediaId={mediaId} size={size} status={status}/>;
    return <Avatar name={name} seed={id} kind="agent" size={size} status={status}/>;
}
function PhotoAvatar({ name, id, mediaId, size, status }: {
    name: string;
    id: string;
    mediaId: string;
    size: number;
    status: 'idle' | 'working' | 'needs-you';
}) { const image = useMediaUrl(mediaId, 'thumb'); return <Avatar name={name} seed={id} src={image.url} size={size} kind="agent" status={status}/>; }
export function Identity({ name, handle, id, mediaId, status, detail }: {
    name: string;
    handle?: string;
    id: string;
    mediaId?: string | null;
    status?: 'idle' | 'working' | 'needs-you';
    detail?: ReactNode;
}) {
    return <div className="agents-identity"><AgentAvatar name={name} id={id} mediaId={mediaId} size={64} status={status}/><div><h2>{name}</h2>{handle && <div className="agents-secondary">@{handle}</div>}{detail}</div></div>;
}
export function Refresh({ onClick, disabled }: {
    onClick(): void;
    disabled?: boolean;
}) { const { w } = useWords(); return <IconButton name="arrow.clockwise" label={w('Refresh')} disabled={disabled} onClick={onClick}/>; }
export function VirtualRows<T>({ items, render, height = 100 }: {
    items: T[];
    render: (item: T, index: number) => ReactNode;
    height?: number;
}) {
    return items.length > 24 ? <Virtuoso className="agents-virtual" style={{ height: Math.min(items.length * height, 600) }} data={items} itemContent={(i, item) => render(item, i)} computeItemKey={(i, item) => text(object(item).id) || i}/> : <>{items.map((item, i) => <div key={text(object(item).id) || i}>{render(item, i)}</div>)}</>;
}
export function BoundedText({ value }: {
    value: string;
}) {
    const { w } = useWords();
    const [expanded, setExpanded] = useState(false);
    const [page, setPage] = useState(0);
    const size = expanded ? 16000 : 2048;
    return <div className="agents-readonly"><pre>{value.slice(page * size, (page + 1) * size)}</pre>{value.length > 2048 && !expanded && <Button size="small" onClick={() => setExpanded(true)}>{w('Show more')}</Button>}{expanded && value.length > size && <div className="agents-actions"><Button disabled={page === 0} onClick={() => setPage(p => p - 1)}>{w('Previous')}</Button><span>{page + 1}/{Math.ceil(value.length / size)}</span><Button disabled={(page + 1) * size >= value.length} onClick={() => setPage(p => p + 1)}>{w('Next')}</Button></div>}</div>;
}
const labels: Record<string, string> = { display_name: 'Name', assistant_id: 'Assistant', conversation_id: 'Chat', project_id: 'Project', member_ids: 'Members', assistant_members: 'Assistants', text: 'Text', at: 'When', reply_to_id: 'Reply to', thread_root_id: 'Thread', send_as: 'Send as', read_scope: 'Read access', send_approval: 'Approval', hears: 'What it hears', directed_by: 'Who directs it here', role_note: 'Role notes', avatar_media_id: 'Photo', kind: 'Kind', revision: 'Revision', expected_revision: 'Revision', time_zone: 'Time zone', starts_at: 'Starts', ends_at: 'Ends', daily_runs: 'Runs a Day', model: 'Model', provider: 'Provider', effort: 'Effort', voice: 'Voice: who it is', values: 'Values: what it weighs', speech: 'Speech: how it talks', state: 'Status', reason: 'Why', expires_at: 'Expires', owner_ids: 'People', owner_handles: 'People', peer: 'Person or assistant handle', enabled: 'Enabled', paused: 'Paused', interval_minutes: 'Interval', follow_chat_settings: 'Follow My Chat Notifications', jobs: 'Schedule', operation: 'Change', profile: 'Profile', read_items: 'Read Items and Calendar', write_own_items: 'Write Own Items', require_item_approval: 'Require Item Approval', allow_label_routing: 'Allow Label Routing', runs: 'Runs', model_calls: 'Model Calls', tokens: 'Tokens', model_time_ms: 'Model Time (ms)', cost_usd: 'Cost (USD)', unreported_tokens: 'Unreported Tokens', unreported_time: 'Unreported Time', unreported_cost: 'Unreported Cost', estimated_calls: 'Estimated Calls', pending_runs: 'Pending Runs', unavailable_runs: 'Unavailable Runs', used_today: 'Used Today', waiting: 'Waiting', resets_at: 'Resets', count: 'Count', action: 'Action', mode: 'Access', scope: 'Scope', target_id: 'Target', target_kind: 'Target Kind', status: 'Status' };
export function Fields({ value, depth = 0, field }: {
    value: unknown;
    depth?: number;
    field?: string;
}) {
    const { w, language } = useWords();
    const [page, setPage] = useState(0);
    if (value === null || value === undefined)
        return <span className="agents-secondary">—</span>;
    if (field === 'reason' && value === 'not_addressed')
        return <span className="agents-value">{w('Skipped: not @mentioned or replied to')}</span>;
    if (typeof value === 'string')
        return value.length > 300 ? <BoundedText value={value}/> : <span className="agents-value">{/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/u.test(value) ? formatDateTime(value, { locale: language }) : field && ['state', 'status', 'read_scope', 'send_approval', 'hears', 'directed_by', 'mode', 'kind'].includes(field) ? w(value) : value}</span>;
    if (typeof value === 'number')
        return <span>{value.toLocaleString(language)}</span>;
    if (typeof value === 'boolean')
        return <span>{w(value ? 'On' : 'Off')}</span>;
    if (depth > 8)
        return <BoundedText value={JSON.stringify(value, null, 2)}/>;
    if (Array.isArray(value))
        return <div className="agents-fields">{value.slice(page * 50, (page + 1) * 50).map((v, i) => <div key={page * 50 + i}><Fields value={v} depth={depth + 1}/></div>)}{value.length > 50 && <div className="agents-actions"><Button disabled={page === 0} onClick={() => setPage(p => p - 1)}>{w('Previous')}</Button><span>{page + 1}/{Math.ceil(value.length / 50)}</span><Button disabled={(page + 1) * 50 >= value.length} onClick={() => setPage(p => p + 1)}>{w('Next')}</Button></div>}</div>;
    return <dl className="agents-fields">{Object.entries(object(value)).map(([key, v]) => <div key={key}><dt>{w(labels[key] ?? key.replaceAll('_', ' '))}</dt><dd><Fields value={v} depth={depth + 1} field={key}/></dd></div>)}</dl>;
}
export function ReadOnlySections({ body }: {
    body: unknown;
}) { const { w } = useWords(); return <>{Object.entries(object(body)).map(([key, value]) => <Card key={key}><DisclosureGroup title={w(labels[key] ?? key.replaceAll('_', ' '))}><Fields value={value}/></DisclosureGroup></Card>)}</>; }
